/**
 * FileManagement — Constants
 *
 * Static values, limits, and configuration for the file management feature.
 */

import SortAZIcon from "../../../../../../../../assets/svg/sort-az-icon";
import SortZAIcon from "../../../../../../../../assets/svg/sort-za-icon";
import SortAscendingIcon from "../../../../../../../../assets/svg/sort-ascending-icon";
import SortDescendingIcon from "../../../../../../../../assets/svg/sort-descending-icon";
import GridIcon from "../../../../../../../../assets/svg/grid-icon";
import ListViewIcon from "../../../../../../../../assets/svg/list-view-icon";
import type {
  KBFileSortKey,
  KBFileSortOrder,
  KBFileType,
  FileDetailsAccordionSection,
  ViewMode,
} from "../types/fileManagement.types";

// ---------------------------------------------------------------------------
// Backend Limits (from kb_file_constants.py)
// ---------------------------------------------------------------------------

export const KB_FILES_LIMITS = {
  /** Maximum size of a single uploaded file (25 MB) */
  MAX_FILE_SIZE_BYTES: 25 * 1024 * 1024,

  /** Maximum number of files in a single upload request */
  MAX_UPLOAD_BATCH: 20,

  /** Maximum folder nesting depth */
  MAX_FOLDER_DEPTH: 15,

  /** Maximum number of children (files + folders) inside a single folder */
  MAX_CHILDREN_PER_FOLDER: 10_000,

  /** Default number of items per page */
  DEFAULT_PAGE_SIZE: 10,

  /** Maximum number of items per page */
  MAX_PAGE_SIZE: 100,

  /** Maximum files allowed in a single ZIP request */
  MAX_ZIP_FILES: 100,

  /** Maximum total size for ZIP download (500 MB) */
  MAX_ZIP_SIZE_BYTES: 500 * 1024 * 1024,
} as const;

// ---------------------------------------------------------------------------
// Allowed File Extensions
// ---------------------------------------------------------------------------

export const ALLOWED_EXTENSIONS = [
  "pdf",
  "docx",
  "doc",
  "xlsx",
  "xls",
  "pptx",
  "ppt",
  "txt",
  "csv",
  "html",
  "jpg",
  "jpeg",
  "png",
  "webp",
  "gif",
  "png",
] as const;

// ---------------------------------------------------------------------------
// Upload
// ---------------------------------------------------------------------------

/** Comma-separated accept string for the file input element. */
export const ACCEPT_STRING = ALLOWED_EXTENSIONS.map((ext) => `.${ext}`).join(
  ","
);

/** Human-readable list of supported formats for the upload description. */
export const SUPPORTED_FORMATS_LABEL = ALLOWED_EXTENSIONS.map(
  (ext) => `.${ext}`
).join(", ");

/** Human-readable max file size label derived from MAX_FILE_SIZE_BYTES. */
export const MAX_FILE_SIZE_LABEL = `${KB_FILES_LIMITS.MAX_FILE_SIZE_BYTES / (1024 * 1024)} MB`;

// ---------------------------------------------------------------------------
// Sorting
// ---------------------------------------------------------------------------

/** Default sort configuration when the page first loads. */
export const DEFAULT_SORT: {
  sort_key: KBFileSortKey;
  sort_order: KBFileSortOrder;
} = {
  sort_key: "updated_at",
  sort_order: "desc",
};

/**
 * Maps the frontend sort field name to the API sort_key.
 * This keeps the column accessorKey decoupled from the API contract.
 */
export const SORT_FIELD_TO_API: Record<string, KBFileSortKey> = {
  name: "name",
  updated_at: "updated_at",
  created_at: "created_at",
};

/** Fields that support column-header sorting in the list view. */
export const SORTABLE_FIELDS = ["name", "updated_at"] as const;

/** Sort dropdown options per sortable column in the list view header. */
export const SORT_OPTIONS: Record<
  string,
  {
    label: string;
    order: KBFileSortOrder;
    Icon: (props: { className?: string }) => JSX.Element;
  }[]
> = {
  name: [
    { label: "Sort A-Z", order: "asc", Icon: SortAZIcon },
    { label: "Sort Z-A", order: "desc", Icon: SortZAIcon },
  ],
  updated_at: [
    { label: "Newest First", order: "desc", Icon: SortDescendingIcon },
    { label: "Oldest First", order: "asc", Icon: SortAscendingIcon },
  ],
};

// ---------------------------------------------------------------------------
// View Modes
// ---------------------------------------------------------------------------

export const VIEW_MODES = {
  LIST: "list",
  GRID: "grid",
} as const;

/** Options rendered by the Grid/List segmented toggle in the action bar. */
export const VIEW_MODE_OPTIONS: {
  id: ViewMode;
  icon: (props: { className?: string }) => JSX.Element;
  label: string;
}[] = [
  { id: "grid", icon: GridIcon, label: "Grid" },
  { id: "list", icon: ListViewIcon, label: "List" },
];

// ---------------------------------------------------------------------------
// Pagination
// ---------------------------------------------------------------------------

/** "Show X rows" dropdown options — must be a subset of what the API allows (max 100). */
export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;

// ---------------------------------------------------------------------------
// Visibility Display Labels
// ---------------------------------------------------------------------------

/** Human-readable labels for the visibility column. */
export const VISIBILITY_LABELS: Record<string, string> = {
  public: "Public",
  private: "Private",
  public_no_index: "Public - noindex",
};

// ---------------------------------------------------------------------------
// File Details Drawer — Accordion Sections
// ---------------------------------------------------------------------------
// Defines the collapsible sections that make up the file details drawer body.

/** Accordion sections rendered in the file details drawer. */
export const FILE_DETAILS_SECTIONS: FileDetailsAccordionSection[] = [
  { id: "access-and-urls", title: "Access and URLs" },
  { id: "file-history", title: "File history" },
  { id: "general", title: "General" },
];

// ---------------------------------------------------------------------------
// File Details Drawer — Visibility Options
// ---------------------------------------------------------------------------
// Options shown in the "File URL Visibility" dropdown in the drawer.

/** Dropdown options for the File URL Visibility field. */
export const VISIBILITY_OPTIONS = [
  {
    label: "Public",
    value: "public",
    description:
      "Anyone with the link can view or download the file. Share or embed it anywhere.",
  },
  {
    label: "Public - noindex",
    value: "public_no_index",
    description:
      "Anyone with the link can access the file, but it won\u2019t appear in search engine results.",
  },
  {
    label: "Private",
    value: "private",
    description:
      "Restricted access. Share only internally or via secure, temporary links.",
  },
] as const;

// ---------------------------------------------------------------------------
// File Details Drawer — Preview Types
// ---------------------------------------------------------------------------
// Drives which preview renderer (image vs. document icon) the drawer uses
// based on the file's type.

/** File types that can render an image preview in the details drawer. */
export const IMAGE_PREVIEW_TYPES: KBFileType[] = [
  "jpg",
  "jpeg",
  "png",
  "webp",
  "gif",
  "png",
];

/** File types that render a centered file-type icon in the details drawer. */
export const DOCUMENT_PREVIEW_TYPES: KBFileType[] = [
  "pdf",
  "docx",
  "doc",
  "xlsx",
  "xls",
  "pptx",
  "ppt",
  "txt",
  "csv",
  "html",
];

// ---------------------------------------------------------------------------
// File Details Drawer — UI Dimensions & Labels
// ---------------------------------------------------------------------------
// Static sizing/overlay values and button labels used by the drawer shell.

export const FILE_DETAILS_DRAWER_WIDTH = "648px";
export const FILE_DETAILS_OVERLAY_OPACITY = 0.3;
export const FILE_DETAILS_DRAWER_TITLE = "File details";
export const FILE_DETAILS_REUPLOAD_BUTTON = "Reupload File";
export const FILE_DETAILS_MORE_BUTTON = "More";
export const FILE_DETAILS_DOWNLOAD_OPTION = "Download";
export const FILE_DETAILS_DOWNLOADING_OPTION = "Downloading...";

// ---------------------------------------------------------------------------
// Breadcrumbs
// ---------------------------------------------------------------------------
// Labels and truncation limits for the folder breadcrumb trail shown at the
// top of the file list.

export const BREADCRUMB_ROOT_LABEL = "Home";
export const BREADCRUMB_ELLIPSIS = "...";
export const BREADCRUMB_MAX_VISIBLE_SEGMENTS = 3;
export const BREADCRUMB_TAIL_COUNT = 2;

// ---------------------------------------------------------------------------
// Toast Messages — Copy URL
// ---------------------------------------------------------------------------
// Success / failure toast copy shown when the user copies a file URL.

export const TOAST_URL_COPIED = "URL copied to clipboard";
export const TOAST_URL_COPY_FAILED = "Failed to copy URL";

// ---------------------------------------------------------------------------
// Toast Messages — File Download
// ---------------------------------------------------------------------------
// Success / failure toast copy shown when the user downloads a file.

export const TOAST_FILE_DOWNLOAD_SUCCESS = "File downloaded successfully";
export const TOAST_FILE_DOWNLOAD_FAILED = "Failed to download file";

export const TOAST_FILE_VISIBILITY_UPDATED = "File visibility updated";
export const TOAST_FILE_EXPIRATION_UPDATED = "File expiration updated";
export const TOAST_FILE_NAME_UPDATED = "File name updated";

// ---------------------------------------------------------------------------
// File Details Drawer — Field Labels
// ---------------------------------------------------------------------------
// Labels and tooltips for the individual fields rendered inside the drawer.

export const FIELD_LABEL_URL_VISIBILITY = "File URL visibility";
export const FIELD_LABEL_FILE_URL = "File URL";
export const FIELD_LABEL_EXPIRATION_DATE = "File expiration date";
export const FIELD_TOOLTIP_EXPIRATION_DATE =
  "Files are automatically sent to Files trash on the date below";
export const FIELD_LABEL_ACTIVITY = "Activity";
export const FIELD_LABEL_PERFORMED_BY = "Performed by";
export const FIELD_LABEL_MADE_ON = "Made on";
export const FIELD_LABEL_CREATED = "Created";
export const FIELD_LABEL_UPDATED = "Updated";
export const FIELD_LABEL_FILE_SIZE = "File size";
export const FIELD_LABEL_DIMENSIONS = "Dimensions";
export const FIELD_LABEL_FILE_TYPE = "File type";
export const FIELD_LABEL_FILE_ID = "File ID";

// ---------------------------------------------------------------------------
// File URL — Link Actions & Hint
// ---------------------------------------------------------------------------
// Labels for the "Open in new window" / "Copy URL" actions and the hint shown
// when no file URL is available yet.

export const LINK_OPEN_NEW_WINDOW = "Open in a new window";
export const LINK_COPY_URL = "Copy URL";
export const FILE_URL_NO_URL_HINT =
  "No URL available. Update file visibility to generate one.";

// ---------------------------------------------------------------------------
// Placeholders & Fallback Values
// ---------------------------------------------------------------------------
// Default placeholder text used for empty date/time inputs and blank fields.

export const PLACEHOLDER_DATE = "Date";
export const PLACEHOLDER_TIME = "Time";
export const PLACEHOLDER_EMPTY = "-";

export const DEFAULT_ACTIVITY_VALUE = "Upload";

// ---------------------------------------------------------------------------
// External Link — window.open Options
// ---------------------------------------------------------------------------
// Target / features passed to window.open when launching a file URL.

export const WINDOW_OPEN_TARGET = "_blank";
export const WINDOW_OPEN_FEATURES = "noopener,noreferrer";

// ---------------------------------------------------------------------------
// File Details Drawer — HTML Iframe Preview
// ---------------------------------------------------------------------------
// Size bounds for the auto-sized HTML preview iframe so short pages don't
// look cramped and long pages don't dominate the drawer (they scroll
// internally instead). Used by HtmlFilePreview.

/** Minimum height of the HTML preview iframe wrapper in pixels. */
export const HTML_PREVIEW_MIN_HEIGHT_PX = 380;

/** Maximum height of the HTML preview iframe wrapper in pixels. */
export const HTML_PREVIEW_MAX_HEIGHT_PX = 480;

/** Initial height shown before the iframe loads (and the cross-origin fallback). */
export const HTML_PREVIEW_DEFAULT_HEIGHT_PX = 380;

/** Extra bottom padding added to the measured content height for breathing room. */
export const HTML_PREVIEW_BOTTOM_PADDING_PX = 16;

// ---------------------------------------------------------------------------
// Row Actions Menu — Items (Three-dots kebab menu)
// ---------------------------------------------------------------------------
// Action items rendered inside the per-row kebab menu. `disabled: true` means
// the action is visible but not yet implemented (muted color, no handler).

export const FILE_ACTION_MENU_ITEMS = [
  { label: "View Details", action: "view_details" },
  // { label: "Move", action: "move" },
  // { label: "Rename", action: "rename" },
  { label: "Copy Link", action: "copy_link" },
  // { label: "Delete", action: "delete" },
] as const;

export const FOLDER_ACTION_MENU_ITEMS = [
  { label: "Open", action: "open" },
  // { label: "Move", action: "move" },
  // { label: "Rename", action: "rename" },
  // { label: "Delete", action: "delete" },
] as const;

export const FOLDER_HEADER_MENU_ITEMS = [
  // { label: "Move", action: "move" },
  // { label: "Rename", action: "rename" },
  // { label: "Delete", action: "delete" },
] as const;

// ---------------------------------------------------------------------------
// Row Actions Menu — Popup Dimensions
// ---------------------------------------------------------------------------
// Used by `calculateRowActionMenuPosition` to place the portalled kebab popup.

export const ROW_ACTION_MENU_WIDTH = 180;
export const ROW_ACTION_MENU_ITEM_HEIGHT = 40;
export const ROW_ACTION_MENU_PADDING = 24;

// ---------------------------------------------------------------------------
// Sort Menu — Popup Dimensions
// ---------------------------------------------------------------------------
// Used by `calculateSortMenuPosition` to place the portalled column sort popup
// so it escapes the table's overflow/stacking context.

export const SORT_MENU_WIDTH = 160;
export const SORT_MENU_ITEM_HEIGHT = 36;
export const SORT_MENU_PADDING = 56;

// ---------------------------------------------------------------------------
// Delete Modal (File + Folder)
// ---------------------------------------------------------------------------

export const DELETE_DESCRIPTION_PREFIX = "Deleting ";
export const DELETE_ERROR_MESSAGE = "Failed to delete";

export const DELETE_FILE_TITLE = "Delete File?";
export const DELETE_FILE_DESCRIPTION =
  " will permanently remove it. Are you sure you want to continue?";
export const DELETE_FILE_SUCCESS = "File deleted successfully";

export const DELETE_FOLDER_TITLE = "Delete Folder?";
export const DELETE_FOLDER_DESCRIPTION =
  " will permanently remove this folder and all the contents inside it. Are you sure you want to continue?";
export const DELETE_FOLDER_SUCCESS = "Folder deleted successfully";
export const DELETE_FOLDER_WARNING =
  "This folder and all contents inside it will be deleted";

export const DELETE_FILE_BUTTON = "Delete File";
export const DELETE_FOLDER_BUTTON = "Delete Folder";
export const DELETE_BUTTON_LOADING = "Deleting...";
export const DELETE_CANCEL_BUTTON = "Cancel";

// ---------------------------------------------------------------------------
// Upload Drawer — Messages & Labels
// ---------------------------------------------------------------------------

export const UPLOAD_MESSAGES = {
  TITLE: "Upload Files",
  BROWSE_FILES: "Browse Files",
  BROWSE_DESCRIPTION: "Select or Drag & Drop files",
  MAX_FILE_SIZE_PREFIX: "Max file size:",
  MAX_FILES_PREFIX: "Max files:",

  SUCCESS_ALL: "All files uploaded successfully",
  ERROR_ALL_FAILED: "All files failed to upload.",
  ERROR_UPLOAD_FAILED: "Upload failed. Please try again.",
  FILE_NAME_HEADER: "File Name",
  FILE_SIZE_HEADER: "File Size",
  CANCEL_BUTTON: "Cancel",
  CLOSE_BUTTON: "Close",
} as const;

// ---------------------------------------------------------------------------
// Rename Folder Modal — Messages
// ---------------------------------------------------------------------------

export const RENAME_FOLDER = {
  TITLE: "Rename Folder",
  LABEL: "Folder name",
  PLACEHOLDER: "e.g, Brand Assets",
  SUCCESS: "Folder renamed successfully",
  ERROR: "Failed to rename folder",
  SAVE: "Save",
  SAVING: "Saving...",
  CANCEL: "Cancel",
} as const;
